"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
function useRevealOnScroll({
  once = true,
  threshold = 0.1
} = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      }
    }, {
      threshold
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, threshold]);
  return {
    ref,
    isVisible
  };
}
const pillars = [{
  id: "psychology",
  title: "Psychologische Praxis & Krisenberatung\xA0",
  target: "PRIVATPERSONEN",
  description: "Persönliche Begleitung in herausfordernden Lebensphasen für neue Klarheit und psychische Entlastung.\xA0",
  products: ["Soforthilfe bei einschneidenden Lebensereignissen und Konflikten", "Persönliches Coaching", "Psychotherapie (Heilpraktikergesetz)", "Zertifizierte Gesundheits- und Präventionskurse", "Gewaltfreie Kommunikation und Mediation"],
  image: "https://excited-caiman.10web.cloud/wp-content/uploads/2026/05/grafik-6.png?v=kro5udporgp",
  color: "#7D5BA6",
  link: "https://praxis-soonwald.de",
  buttonText: "ZUR PRAXIS"
}, {
  id: "organization",
  title: "Organisationsberatung & Coaching",
  target: "ORGANISATIONEN, TEAMS & FÜHRUNGSKRÄFTE",
  description: "Systemische Beratung für gesunde Strukturen und eine tragfähige Kultur in Teams und Einrichtungen.",
  products: ["Führungskräfte-Coaching", "Berufsbezogenes Coaching", "Prozessbegleitung", "Teamentwicklung", "Supervision", "Mediation und Konfliktberatung", "Inhouse-Fortbildungen", "Fach- und Impulsvorträge"],
  image: "https://excited-caiman.10web.cloud/wp-content/uploads/2026/05/grafik-5.png?v=1et2dx59qnq",
  color: "#4A6A8A",
  link: "https://christ-coaching.de",
  buttonText: "ZUR ORGANISATIONS-BERATUNG"
}, {
  id: "taichi",
  title: "Tai Chi Schule & Achtsamkeit",
  target: "KÖRPERLICHE VITALITÄT & INNERE RUHE",
  description: "Traditionelle Bewegungskunst und Geistesschulung zur Förderung von Ruhe und Vitalität. ",
  products: ["Einzel- und Gruppenunterricht", "Tai Chi Hand- und Waffenformen", "Qi Gong", "Yoga", "Achtsamkeit und Vipassana-Meditation", "Zertifizierte Gesundheits- und Präventionskurse"],
  image: "https://excited-caiman.10web.cloud/wp-content/uploads/2026/05/grafik-7.png?v=iek6hj1yqnc",
  color: "#2A5D37",
  link: "https://tai-chi-soonwald.de",
  buttonText: "ZUR TAI CHI SCHULE"
}, {
  id: "wilderness",
  title: "Wildnisschule Soonwald",
  target: "NATURVERBINDUNG & ERDUNG",
  description: "Naturverbindung als Kraftquelle für echte Erdung und die Rückbesinnung auf das Wesentliche.",
  products: ["Wildniskurse", "Inhouse-Fortbildungen zu Natur-, Wildnis- und Abenteuerpädagogik", "Outdoor-Teamentwicklungen", "Ferienprogramme für Kinder", "Wildnisangebote für Einrichtungen und Kitas"],
  image: "https://excited-caiman.10web.cloud/wp-content/uploads/2026/05/grafik-8.png?v=8n4ddcs816q",
  color: "#845C3E",
  link: "https://wildnisschule-soonwald.de",
  buttonText: "ZUR WILDNISSCHULE"
}];
export default function ExpertiseInAction() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section id="einblicke-in-den-soonwald" className="w-full bg-background text-foreground py-20 md:py-32 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`mb-16 md:mb-20 max-w-4xl transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-primary"></div>
            <span className="font-sans text-sm uppercase tracking-widest text-primary font-semibold">Resilienz auf vier Wegen begegnen</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-foreground mb-6 leading-tight">Gemeinsam Wege finden, <br />die wirklich tragen.</h2>
          <p className="font-sans text-lg md:text-xl text-muted-foreground leading-relaxed">Ob Sie für sich selbst nach Klarheit suchen, als Verantwortliche Ihr Team stärken wollen oder als Partner fachliche Impulse brauchen: Wir begleiten Sie auf Ihrem Weg zu innerer Stärke. Dafür verbinden wir die Expertise aus den Fachbereichen Psychologie, Organisationsberatung, Körper, Geist, Natur – und arbeiten mit Ihnen sowohl in unseren Praxis- und Naturräumen im Soonwald als auch flexibel, mobil und digital direkt in Ihrem Kontext.</p>
        </div>
        
        <div className="mb-10 md:mb-14 flex items-center gap-3">
          <span className="font-sans text-xs md:text-sm uppercase tracking-widest text-primary font-bold bg-primary/10 px-4 py-2 rounded-full">
            Unsere Sektionen
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {pillars.map((pillar, index) => {
          const {
            ref,
            isVisible
          } = useRevealOnScroll({
            threshold: 0.1
          });
          return <div key={pillar.id} ref={ref} className={`group relative flex flex-col bg-card border border-border rounded-3xl overflow-hidden transition-all duration-700 ease-out hover:shadow-xl cursor-pointer ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${index * 100}ms`,
            borderLeft: `6px solid ${pillar.color}`
          }}>
                <Link to={pillar.link} newTab className="absolute inset-0 z-20" aria-label={pillar.title} />
                
                <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                  <img src={pillar.image} data-wvc-srcset={`${pillar.image}-wvc-srcset`} data-wvc-sizes={`${pillar.image}-wvc-sizes`} alt={pillar.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/50 via-black/20 to-transparent p-4 z-10">
                    <span className="inline-block px-3 py-1.5 text-[10px] md:text-xs font-sans font-bold tracking-wider text-white rounded-full backdrop-blur-sm shadow-sm uppercase" style={{
                  backgroundColor: `${pillar.color}E6`
                }}>
                      {pillar.target}
                    </span>
                  </div>
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-foreground mb-4 leading-tight min-h-[3rem]">{pillar.title}</h3>
                  <p className="font-sans text-sm md:text-base text-muted-foreground mb-6 leading-relaxed">{pillar.description}</p>
                  
                  <ul className="space-y-3 mb-8 flex-1 text-left">
                    {pillar.products.map((product, pIndex) => <li key={pIndex} className="flex items-start gap-2.5 text-xs md:text-sm text-muted-foreground leading-snug">
                        <span className="h-1.5 w-1.5 rounded-full shrink-0 mt-2 transition-transform duration-300 group-hover:scale-125" style={{
                    backgroundColor: pillar.color
                  }} />
                        <span>{product}</span>
                      </li>)}
                  </ul>
                  
                  <div className="w-full h-auto min-h-[64px] rounded-lg py-4 px-3 font-sans font-bold uppercase text-[9px] sm:text-[10px] tracking-widest transition-all duration-300 flex items-center justify-center text-center leading-tight text-white group-hover:opacity-90" style={{
                backgroundColor: pillar.color
              }}>
                    <span className="flex-1 px-1 break-words whitespace-normal">{pillar.buttonText}</span>
                    <ArrowRight className="h-3 w-3 flex-shrink-0" />
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}